import {
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  message,
  Select,
} from 'antd';
import dayjs from 'dayjs';
import React, { useEffect } from 'react';
import { MaturityScheduleResponse } from '../../model/MaturitySchedule.model';
import MaturityScheduleService from '../../service/maturity-schedule-service/MaturitySchedule.service';
import EmployeeService from '../../service/employee-service/Employee.service';
import EmployeeResponse from '../../model/Employee.model';
import { useIsAdmin } from '../../hook/useRole';

interface EditMaturityScheduleModalProps {
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
  maturitySchedule: MaturityScheduleResponse;
  employeeId: number;
}

interface EditFormData {
  repaymentAmount: number;
  feeApplied?: number;
  dueDate: dayjs.Dayjs;
  notes?: string;
  employeeIds?: number[];
}

const EditMaturityScheduleModal: React.FC<EditMaturityScheduleModalProps> = ({
  visible,
  onClose,
  onSuccess,
  maturitySchedule,
  employeeId,
}) => {
  const [form] = Form.useForm<EditFormData>();
  const [loading, setLoading] = React.useState(false);
  const [employeeOptions, setEmployeeOptions] = React.useState<
    EmployeeResponse[]
  >([]);
  const isAdmin = useIsAdmin();

  // Initialize form with current values
  useEffect(() => {
    if (visible && maturitySchedule) {
      form.setFieldsValue({
        repaymentAmount: maturitySchedule.repaymentAmount,
        feeApplied: maturitySchedule.feeApplied || undefined,
        dueDate: dayjs(maturitySchedule.dueDate),
        notes: maturitySchedule.notes || '',
        employeeIds: maturitySchedule.employees?.map((e) => e.id) || [],
      });
      // Fetch employee list
      EmployeeService.getEmployeeList().then(setEmployeeOptions);
    }
  }, [visible, maturitySchedule, form]);

  const handleSubmit = async (values: EditFormData) => {
    try {
      setLoading(true);

      const updateData = {
        repaymentAmount: values.repaymentAmount,
        feeApplied:
          values.feeApplied !== undefined && values.feeApplied !== null
            ? values.feeApplied
            : null,
        dueDate: values.dueDate.format('YYYY-MM-DD'),
        notes: values.notes || '',
        employeeIds: values.employeeIds || [],
      };

      const response = await MaturityScheduleService.updateMaturitySchedule(
        maturitySchedule.id,
        updateData,
        employeeId
      );

      if (response.success) {
        message.success('Cập nhật kỳ đáo hạn thành công');
        form.resetFields();
        onSuccess();
        onClose();
      } else {
        message.error(response.message || 'Có lỗi xảy ra khi cập nhật');
      }
    } catch (error: any) {
      console.error('Error updating maturity schedule:', error);
      message.error(
        error.response?.data?.message || 'Có lỗi xảy ra khi cập nhật kỳ đáo hạn'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND',
    }).format(value);
  };

  return (
    <Modal
      title="Chỉnh sửa kỳ đáo hạn"
      open={visible}
      onOk={form.submit}
      onCancel={handleCancel}
      confirmLoading={loading}
      width={600}
      okText="Cập nhật"
      cancelText="Hủy"
    >
      <div className="mb-4 p-4 bg-blue-50 rounded-lg">
        <h4 className="text-sm font-semibold text-blue-800 mb-2">
          Thông tin thẻ tín dụng
        </h4>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-gray-600">Số thẻ: </span>
            <span className="font-medium">
              ***{maturitySchedule.creditCard?.cardNumber?.slice(-4)}
            </span>
          </div>
          <div>
            <span className="text-gray-600">Chủ thẻ: </span>
            <span className="font-medium">
              {maturitySchedule.creditCard?.cardHolderName}
            </span>
          </div>
          <div>
            <span className="text-gray-600">Ngân hàng: </span>
            <span className="font-medium">
              {maturitySchedule.creditCard?.bank?.name}
            </span>
          </div>
          <div>
            <span className="text-gray-600">Loại thẻ: </span>
            <span className="font-medium">
              {maturitySchedule.creditCard?.cardType?.name}
            </span>
          </div>
        </div>
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        requiredMark={false}
      >
        <Form.Item
          label="Số tiền đáo hạn"
          name="repaymentAmount"
          rules={[
            { required: true, message: 'Vui lòng nhập số tiền đáo hạn' },
            { type: 'number', min: 1, message: 'Số tiền phải lớn hơn 0' },
          ]}
        >
          <InputNumber
            style={{ width: '100%' }}
            placeholder="Nhập số tiền đáo hạn"
            formatter={(value) =>
              `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
            }
            parser={(value) => value!.replace(/\$\s?|(,*)/g, '')}
            addonAfter="VND"
          />
        </Form.Item>

        <Form.Item
          label="Phí thu (%)"
          name="feeApplied"
          rules={[
            { type: 'number', min: 0, message: 'Phí thu không được âm' },
            {
              type: 'number',
              max: 100,
              message: 'Phí thu không được vượt quá 100%',
            },
          ]}
        >
          <InputNumber
            style={{ width: '100%' }}
            placeholder="Nhập phí thu (tùy chọn)"
            step={0.1}
            precision={2}
            addonAfter="%"
          />
        </Form.Item>

        <Form.Item
          label="Hạn đáo"
          name="dueDate"
          rules={[{ required: true, message: 'Vui lòng chọn hạn đáo' }]}
        >
          <DatePicker
            style={{ width: '100%' }}
            placeholder="Chọn hạn đáo"
            format="DD/MM/YYYY"
          />
        </Form.Item>

        <Form.Item label="Ghi chú" name="notes">
          <Input.TextArea
            rows={3}
            placeholder="Nhập ghi chú (tùy chọn)"
            maxLength={500}
            showCount
          />
        </Form.Item>

        <Form.Item
          label="Người phụ trách"
          name="employeeIds"
          rules={[{ required: true, message: 'Vui lòng chọn người phụ trách' }]}
        >
          <Select
            mode="multiple"
            placeholder="Chọn nhân viên phụ trách"
            options={employeeOptions.map((e) => ({
              label: e.name,
              value: e.id,
            }))}
            showSearch
            optionFilterProp="label"
            style={{ width: '100%' }}
            disabled={!isAdmin}
          />
        </Form.Item>
      </Form>

      {/* Warning message for completed transactions */}
      {(maturitySchedule.totalDepositAmount &&
        maturitySchedule.totalDepositAmount > 0) ||
      (maturitySchedule.totalWithdrawalAmount &&
        maturitySchedule.totalWithdrawalAmount > 0) ? (
        <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
          <div className="text-yellow-800 text-sm">
            <strong>Lưu ý:</strong> Kỳ đáo hạn này đã có giao dịch. Một số
            trường có thể không được phép thay đổi.
          </div>
        </div>
      ) : null}
    </Modal>
  );
};

export default EditMaturityScheduleModal;
